/**
 * ag-theme-capitol — the AG Grid side of the table surface (P3-01).
 *
 * Every colour is a `var()` into the grid tokens in index.css, which is also
 * where StandardTable's CSS reads from, so the two stop being independent
 * descriptions of the same table.
 *
 * They had already drifted. This file's report header was #374151 — Tailwind's
 * default gray-700 — against the #334155 the CSS uses, so an AG Grid report and
 * a hand-rolled report table rendered subtly different greys. Six more of its
 * greys (#D1D5DB, #4B5563, #E5E7EB, #6B7280, #1F2937, #A88EBD) were Tailwind
 * defaults that survived the P1-02 / P1-04 palette work because nothing lints a
 * .ts file for hex.
 *
 * Colours need no light/dark duplication here: AG Grid's colorValueToCss passes
 * any string through untouched, so `'var(--grid-border)'` lands verbatim in the
 * generated `--ag-border-color` and resolves against whichever `.dark` scope the
 * grid sits in. Only `browserColorScheme` genuinely differs per mode — it drives
 * the browser's own scrollbar and form-control rendering, not a CSS value.
 */
import { themeQuartz } from 'ag-grid-community';

/** Shared by both header identities and both modes. */
const SURFACE = {
  headerTextColor: '#FFFFFF',
  headerFontSize: 11,
  headerFontWeight: 700,
  headerColumnResizeHandleColor: 'rgba(255,255,255,0.3)',

  backgroundColor: 'var(--grid-surface)',
  foregroundColor: 'var(--grid-foreground)',

  borderColor: 'var(--grid-border)',
  borderRadius: 0,
  wrapperBorderRadius: 8,
  columnBorder: false,

  rowHoverColor: 'var(--grid-row-hover)',
  oddRowBackgroundColor: 'var(--grid-surface)',

  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  fontSize: 14,
  spacing: 8,
};

/**
 * Data tables: brand purple header — the live record, matching the navigation —
 * plus selection and checkboxes, which reports have no use for. Row separators
 * are deliberately heavier than the frame: that is what makes a dense bill list
 * scannable.
 */
const DATA = {
  ...SURFACE,
  headerBackgroundColor: 'var(--grid-header-bg)',
  accentColor: 'var(--grid-accent)',
  selectedRowBackgroundColor: 'var(--grid-row-selected)',
  cellHorizontalPadding: 20,
  rowBorder: { width: 2, color: 'var(--grid-border)' },
  checkboxCheckedBackgroundColor: 'var(--grid-accent)',
  checkboxCheckedBorderColor: 'var(--grid-accent)',
  checkboxCheckedShapeColor: '#FFFFFF',
  checkboxUncheckedBackgroundColor: 'var(--grid-checkbox-unchecked-bg)',
  checkboxUncheckedBorderColor: 'var(--grid-checkbox-unchecked-border)',
  checkboxBorderWidth: 2,
};

/**
 * Reports: grey header, so an exported or shared report never reads as live
 * data at a glance. Airier and more lightly ruled than a data table — a report
 * is read, not worked in. Both differences are kept from the previous themes.
 */
const REPORT = {
  ...SURFACE,
  headerBackgroundColor: 'var(--grid-report-header-bg)',
  foregroundColor: 'var(--grid-report-foreground)',
  accentColor: 'var(--grid-report-accent)',
  cellHorizontalPadding: 24,
  rowBorder: { width: 1, color: 'var(--grid-border)' },
};

export const billTableLightTheme = themeQuartz.withParams({ ...DATA, browserColorScheme: 'light' });
export const billTableDarkTheme = themeQuartz.withParams({ ...DATA, browserColorScheme: 'dark' });
export const reportGridLightTheme = themeQuartz.withParams({ ...REPORT, browserColorScheme: 'light' });
export const reportGridDarkTheme = themeQuartz.withParams({ ...REPORT, browserColorScheme: 'dark' });
